/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';
import { UnstyledButton } from './UnstyledButton';

export interface SegmentedControlItem<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  testId?: string;
}

export interface SegmentedControlProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  items: SegmentedControlItem<T>[];
  variant?: 'solid' | 'pill';
  size?: 'sm' | 'md';
  ariaLabel?: string;
  className?: string;
  testId?: string;
  /**
   * The accessibility semantics the control carries.
   *
   * `tab` (default) draws a `tablist` of `tab`s, which is right when each
   * segment switches the view below it. `radio` draws a `radiogroup` of
   * `radio`s, which is right when the segments answer one question rather
   * than navigate: a billing cycle is a choice, not a destination, so a
   * screen reader (and the checkout E2E) looks for the radio named after it.
   */
  selectionRole?: 'tab' | 'radio';
}

const sizeClasses: Record<string, Record<string, string>> = {
  solid: {
    sm: 'px-(--space-3) py-(--space-1) text-xs',
    md: 'px-(--space-3) py-(--space-2) text-[13px]',
  },
  pill: {
    sm: 'px-3 py-1 text-[12px]',
    md: 'px-4 py-1.5 text-[13px]',
  },
};

const containerClasses: Record<string, string> = {
  solid: 'inline-flex items-center w-fit max-w-full self-start overflow-x-auto rounded-(--radius-md) border border-[var(--border-subtle)] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
  pill: 'inline-flex items-center gap-1 p-1 w-fit max-w-full self-start rounded-(--radius-md) bg-[var(--surface-tertiary)] overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
};

/**
 * Computes the Tailwind class string for a single segmented-control item,
 * varying styling by `variant` and whether the item is currently active.
 *
 * @param variant Visual style, either `pill` or the default `solid`.
 * @param active Whether the item represents the selected value.
 * @returns The class string to apply to the item element.
 */
function itemClasses(variant: string, active: boolean): string {
  if (variant === 'pill') {
    return active
      ? 'bg-[var(--surface-primary)] text-[var(--text-primary)] shadow-sm font-semibold rounded-(--radius-sm)'
      : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] font-medium rounded-(--radius-sm)';
  }
  return active
    ? 'bg-[var(--seed-base)] font-semibold text-white'
    : 'bg-[var(--surface-primary)] font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)]';
}

/**
 * Shared segmented control for single-select tab/toggle patterns.
 *
 * Variants:
 * - `solid` (default): bordered container with seed-base fill on active item
 * - `pill`: surface-tertiary container with elevated white-pill on active item
 */
export function SegmentedControl<T extends string = string>({
  value,
  onChange,
  items,
  variant = 'solid',
  size = 'md',
  ariaLabel,
  className,
  testId,
  selectionRole = 'tab',
}: SegmentedControlProps<T>) {
  const asRadio = selectionRole === 'radio';
  return (
    <div
      role={asRadio ? 'radiogroup' : 'tablist'}
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(containerClasses[variant], className)}
    >
      {items.map((item) => {
        const active = value === item.value;
        return (
          <UnstyledButton
            key={item.value}
            type="button"
            role={asRadio ? 'radio' : 'tab'}
            aria-selected={asRadio ? undefined : active}
            aria-checked={asRadio ? active : undefined}
            data-testid={item.testId}
            onClick={() => onChange(item.value)}
            className={cn(
              sizeClasses[variant][size],
              'flex items-center justify-center gap-(--space-1)',
              'font-[var(--font-family)] transition-all duration-(--duration-fast)',
              'focus-visible:ring-inset',
              itemClasses(variant, active),
            )}
          >
            {item.icon}
            {item.label}
          </UnstyledButton>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
